import React from 'react';
import isNil from '~/_base/functions/isNil';
import isObjectEmpty from '~/_base/functions/isObjectEmpty';
import loadGoogleMapsApi from 'load-google-maps-api';
import moment from 'moment';
import cache from '../cache/cache';
import { ILoader } from '~/_base/components/helixIcons';
import mapIconCreate from '../images/marker-create.png';
import mapIconUpdate from '../images/marker-update.png';
import mapIconSend from '../images/marker-send.png';
import mapIconDelete from '../images/marker-delete.png';
import mapIconDrawing from '../images/marker-drawing.png';
import mapIconUnknown from '../images/marker-unknown.png';
import getTransactionSubtype from '../functions/getTransactionSubtype';
import {
  SUBTYPE_NEW,
  SUBTYPE_CREATE,
  SUBTYPE_UPDATE,
  SUBTYPE_SEND,
  SUBTYPE_DELETE,
  SUBTYPE_DRAWING,
  SUBTYPE_UNKNOWN,
} from '../consts/consts';

export default class FeatureMap extends React.Component {
  constructor() {
    super();
    this.map;
    this.openInfoWindowlistenerHandle;
    this.closeInfoWindowListenerHandle;

    this.state = {
      googleApiLoaded: false,
    };
  }

  componentDidMount() {
    const loadApi = this.initMapAPI();

    loadApi.then(resp => {
      this.setState({
        googleApiLoaded: true,
      });

      this.mapStartUp(this.props);
    });
  }

  componentDidUpdate(prevProps) {
    if (!this.state.googleApiLoaded) return;

    const featuresChanged = this.dataChanged(prevProps.mapData.features, this.props.mapData.features);
    const gettingChanged = prevProps.getting !== this.props.getting;

    if (featuresChanged || gettingChanged) {
      this.removeDataLayer(() => this.mapUpdate(this.props));
    }
  }

  render() {
    // console.log('Render', this.props)
    return (
      <div id="googleMapWrapper">
        {this.props.getting ? (
          <div className="mapLoading">
            <ILoader fontSize="large" />
          </div>
        ) : null}

        <div id="googleMapContainer" />
      </div>
    );
  }

  mapStartUp(props) {
    // console.log('mapStartUp', props.mapData.features.length)
    this.loadMap();
    this.setMapBoundary(props.boundingRectangle);
    this.setMarkerStyles();
    this.bindToMarkers();
  }

  mapUpdate(props) {
    // console.log('mapUpdate', props.mapData.features.length)
    this.loadDataLayer(props.mapData);
    this.setMarkerStyles();
    this.bindToMarkers();
    this.setMapBoundary(props.boundingRectangle);
  }

  initMapAPI() {
    return new Promise((resolve, reject) => {
      if (typeof google === 'object' && typeof google.maps === 'object') {
        resolve('already loaded');
        return;
      }

      loadGoogleMapsApi({ key: process.env.GOOGLE_MAPS_API_KEY || '' })
        .then(googleMaps => {
          resolve('google api loaded');
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  loadMap() {
    const mapOptions = {
      // zoom: 18,
      // center: new google.maps.LatLng(40.714364, -74.005972), // currently set to NYC
      mapTypeId: google.maps.MapTypeId.ROADMAP,
    };

    this.map = new google.maps.Map(
      document.querySelector('#googleMapContainer'),
      mapOptions
    );

    google.maps.event.addListener(this.map, 'bounds_changed', () => {
      this.storeBoundingRectangle(this.map.getBounds());
    });
  }

  storeBoundingRectangle(bounds) {
    const projectId = this.props.projectId;
    if (typeof Storage !== undefined && bounds) {
      localStorage.setItem(
        `ape_liveview_bounds_${projectId}`,
        JSON.stringify(bounds.toJSON())
      );
    }
  }

  loadDataLayer(featureCollection) {
    //
    if (isObjectEmpty(featureCollection)) {
      return;
    }

    if (isObjectEmpty(this.map)) {
      return;
    }

    // add a data layer
    this.map.data.addGeoJson(featureCollection);
    this.addInfoWindowsToDataLayer();
  }

  removeDataLayer(callback) {
    if (this.map !== undefined && this.map.data !== undefined) {
      this.map.data.forEach(feature => {
        this.map.data.remove(feature);
      });
    }
    callback();
  }

  setMapBoundary(boundingRectangle) {
    let bottom, left, top, right;

    if (isObjectEmpty(boundingRectangle) || isNil(boundingRectangle.left)) {
      return;
    }

    bottom = boundingRectangle.bottom;
    left = boundingRectangle.left;
    top = boundingRectangle.top;
    right = boundingRectangle.right;

    let boundingLatLngs = [];
    boundingLatLngs.push({ lat: bottom, lng: left });
    boundingLatLngs.push({ lat: top, lng: right });

    let bounds = new google.maps.LatLngBounds();

    for (let l of boundingLatLngs) {
      bounds.extend(l);
    }

    if (this.map) {
      this.map.fitBounds(bounds);
    }
  }

  addInfoWindowsToDataLayer() {
    let infoWindow = new google.maps.InfoWindow();

    // clear any listeners on the infoWindow or else the listener will be added each time the component refreshes.
    if (!isNil(this.openInfoWindowlistenerHandle)) {
      google.maps.event.removeListener(this.openInfoWindowlistenerHandle);
    }

    if (!isNil(this.closeInfoWindowListenerHandle)) {
      google.maps.event.removeListener(this.closeInfoWindowListenerHandle);
    }

    const wantedProperties = ['href', 'name', 'type', 'subtype', 'timestamp'];
    function bestFeature(features, wantedProperties) {
      // Return the nested feature object with the highest number of wanted properties.
      if (!(typeof features === 'object')) return;
      let bestFeatureKey;
      let bestScore = 0;
      for (var key in features) {
        let feature = features[key];
        var featureScore = 0;
        for (var wProp of wantedProperties) {
          if (feature[wProp]) {
            featureScore++;
            if (featureScore > bestScore) {
              bestScore = featureScore;
              bestFeatureKey = key;
            }
          }
        }
      }
      if (bestFeatureKey) return features[bestFeatureKey];
    }

    // add click event for marker's infoWindow
    this.openInfoWindowlistenerHandle = this.map.data.addListener(
      'click',
      event => {
        const feature = bestFeature(event.feature, wantedProperties);
        if (!feature) return;
        const html = `
                <div><a href='${feature.href || '#'}'>${feature.name ||
          'Name unavailable'}</a></div>
                <div>${feature.type}</div>
                <div>${feature.subtype}</div>
                <div>${moment(feature.timestamp).format(
                  'DD MMM YYYY HH:mm'
                )}</div>
            `;
        infoWindow.setContent(html);
        infoWindow.setPosition(event.feature.getGeometry().get());

        infoWindow.setOptions({
          pixelOffset: new google.maps.Size(0, -30),
        });

        infoWindow.open(this.map);

        this.props.actions.selectFeature(feature.id);
      }
    );

    this.closeInfoWindowListenerHandle = google.maps.event.addListener(
      infoWindow,
      'closeclick',
      event => {
        this.props.actions.clearFeatureSelection();
      }
    );
  }

  setMarkerStyles() {
    if (isObjectEmpty(this.map)) {
      return;
    }

    function featureWithSubtype(features) {
      // Return a nested feature object that has a 'subtype' property.
      if (!(typeof features === 'object')) return;
      return features[Object.keys(features).find(x => features[x].subtype)];
    }

    this.map.data.setStyle(event => {
      const feature = featureWithSubtype(event);
      const subtype = feature && getTransactionSubtype(feature.subtype);
      switch (subtype) {
        case SUBTYPE_NEW:
          return { icon: mapIconCreate };
        case SUBTYPE_CREATE:
          return { icon: mapIconCreate };
        case SUBTYPE_UPDATE:
          return { icon: mapIconUpdate };
        case SUBTYPE_SEND:
          return { icon: mapIconSend };
        case SUBTYPE_DELETE:
          return { icon: mapIconDelete };
        case SUBTYPE_DRAWING:
          return { icon: mapIconDrawing };
        case SUBTYPE_UNKNOWN:
          return { icon: mapIconUnknown };
        default:
          return { icon: mapIconUnknown };
      }
    });
  }

  bindToMarkers() {
    window.showMapInfoWindow = id => {
      const marker = this.map.data.getFeatureById(id);
      google.maps.event.trigger(this.map.data, 'click', {
        feature: marker,
      });
    };
  }

  // checks if features have changed
  dataChanged(a = [], b = []) {
    if (a.length !== b.length) {
      return true;
    }

    for (let i = 0; i < a.length; i++) {
      // const feature = a[i];
      if (a[i].id !== b[i].id) {
        return true;
      }
    }
    return false;
  }
}
